/**
 * Converts a font weight string to its numeric equivalent.
 * 
 * @param weight - Font weight string (e.g., "regular", "500", "bold")
 * @returns Numeric weight value
 * 
 * @example
 * normalizeWeight("regular") // "400"
 * normalizeWeight("bold") // "700"
 * normalizeWeight("500") // "500"
 */
export const normalizeWeight = (weight: string): string => {
  if (weight === "regular") return "400";
  if (weight === "bold") return "700";
  return weight;
};

/**
 * Filters font variants to get only non-italic weights.
 * 
 * @param variants - Array of font variant strings
 * @returns Array of non-italic weight strings, normalized
 * 
 * @example
 * getNonItalicWeights(["regular", "500italic", "700", "900italic"]) // ["400", "700"]
 */
export const getNonItalicWeights = (variants: string[]): string[] => {
  return variants
    .filter((v) => !v.includes("italic"))
    .map(normalizeWeight);
};

/**
 * Filters font variants to get only italic weights.
 * 
 * @param variants - Array of font variant strings
 * @returns Array of italic weight strings, normalized and cleaned
 * 
 * @example
 * getItalicWeights(["regular", "500italic", "700", "900italic"]) // ["500", "900"]
 */
export const getItalicWeights = (variants: string[]): string[] => {
  return variants
    .filter((v) => v.includes("italic"))
    .map((v) => {
      const cleaned = v.replace("italic", "");
      return normalizeWeight(cleaned === "" ? "regular" : cleaned);
    });
};

/**
 * Generates a weight string for Google Fonts API from an array of font weights.
 * Converts weight names to numbers, removes duplicates, sorts them, and creates
 * ranges only when beneficial (3+ consecutive weights differing by 100).
 * This follows Google Fonts API guidelines to avoid overlapping/touching tuples.
 * 
 * @param weights - Array of font weight strings (e.g., ["regular", "500", "700", "900"])
 * @returns String representation of weights and ranges separated by semicolons
 * 
 * @example
 * generateWeightsString(["regular", "500", "700"]) // "400;500;700"
 * generateWeightsString(["400", "500", "600", "700"]) // "400..700"
 * generateWeightsString(["regular", "700", "900"]) // "400;700;900"
 */
export const generateWeightsString = (weights: string[]): string => {
  if (!weights.length) return "";
  
  const numWeights = weights
    .map((w) => parseInt(normalizeWeight(w), 10))
    .sort((a, b) => a - b);
  
  const unique = [...new Set(numWeights)];
  
  // For Google Fonts API compliance, only create ranges when we have 3+ consecutive weights
  // This avoids overlapping/touching tuples as per API specification
  const result: string[] = [];
  let i = 0;
  
  while (i < unique.length) {
    let rangeStart = unique[i];
    let rangeEnd = unique[i];
    let consecutiveCount = 1;
    
    // Count consecutive weights (differing by 100)
    while (i + consecutiveCount < unique.length && 
           unique[i + consecutiveCount] === unique[i + consecutiveCount - 1] + 100) {
      rangeEnd = unique[i + consecutiveCount];
      consecutiveCount++;
    }
    
    // Only create a range if we have 3+ consecutive weights to make it worthwhile
    if (consecutiveCount >= 3) {
      result.push(`${rangeStart}..${rangeEnd}`);
    } else {
      // Add individual weights
      for (let j = 0; j < consecutiveCount; j++) {
        result.push(`${unique[i + j]}`);
      }
    }
    
    i += consecutiveCount;
  }
  
  return result.join(";");
};

/**
 * Generates Google Fonts API parameter string for the given font variants.
 * Handles both italic and non-italic weights intelligently.
 * Follows Google Fonts API specification for axis tuples.
 * 
 * @param variants - Array of font variant strings
 * @returns Google Fonts API parameter string
 * 
 * @example
 * generateFontParam(["regular", "500", "700italic"]) // "ital,wght@0,400;0,500;1,700"
 * generateFontParam(["400", "700"]) // "wght@400;700"
 * generateFontParam(["400italic", "700italic"]) // "ital,wght@1,400;1,700"
 */
export const generateFontParam = (variants: string[]): string => {
  const nonItalic = getNonItalicWeights(variants);
  const italic = getItalicWeights(variants);
  
  if (nonItalic.length > 0 && italic.length > 0) {
    // When we have both italic and non-italic, we need to format as tuples
    const nonItalicTuples = nonItalic.map(w => `0,${w}`).join(';');
    const italicTuples = italic.map(w => `1,${w}`).join(';');
    return `ital,wght@${nonItalicTuples};${italicTuples}`;
  } else if (nonItalic.length > 0) {
    const nonStr = generateWeightsString(nonItalic);
    return `wght@${nonStr}`;
  } else if (italic.length > 0) {
    const italicWeightsString = generateWeightsString(italic);
    // Prefix each segment with the italic axis value (1,)
    const italicTuples = italicWeightsString
      .split(';')
      .filter(Boolean)
      .map((segment) => `1,${segment}`)
      .join(';');
    return `ital,wght@${italicTuples}`;
  }
  
  return "";
};

/**
 * Generates a complete Google Fonts URL for the given font family and variants.
 * 
 * @param family - Font family name
 * @param variants - Array of font variant strings
 * @param subsets - Array of font subsets (defaults to ["latin"])
 * @returns Complete Google Fonts URL
 * 
 * @example
 * generateGoogleFontsUrl("Roboto", ["regular", "500", "700italic"], ["latin", "latin-ext"])
 * // "https://static.talentech.io/googlefonts/css2?family=Roboto:ital,wght@0,400;0,500;1,700&subset=latin,latin-ext&display=swap"
 */
export const generateGoogleFontsUrl = (
  family: string,
  variants: string[],
  subsets: string[] = ["latin"]
): string => {
  const familyParam = family.replace(/ /g, "+");
  const param = generateFontParam(variants);
  
  let url = `https://static.talentech.io/googlefonts/css2?family=${familyParam}`;
  if (param) url += `:${param}`;
  
  const subsetsParam = subsets.join(",");
  if (subsetsParam && subsetsParam !== "latin") {
    url += `&subset=${subsetsParam}`;
  }
  
  url += "&display=swap";
  return url;
};